import { useNode, Element } from "@craftjs/core";
import React, { useRef } from "react";
import { Box, Typography } from "@mui/material";
import { useResponsive } from "../../hooks/useResponsive";
import { Text } from "./Text";
import { getTestimonialStyles } from "../../config/styleConfig";
import { selectedOutlineSx, labelStyle } from "../../config/settingsStyles";
import { ImageUpload } from "../ui/ImageUpload";
import { ColorField, SliderField, CheckboxField, SettingsSection } from "../ui/form/SettingsField";

export interface TestimonialProps {
  quote: string;
  authorName: string;
  authorTitle?: string;
  authorImage?: string;
  rating?: number;
  backgroundColor?: string;
  textColor?: string;
  showQuotes?: boolean;
  borderRadius?: number;
}

export const Testimonial = ({
  quote = "The care I received at this clinic was exceptional.",
  authorName = "Sarah M.",
  authorTitle,
  authorImage,
  rating = 5,
  backgroundColor = "#ffffff",
  textColor = "#1f2937",
  showQuotes = true,
  borderRadius = 12,
}: TestimonialProps) => {
  const {
    connectors: { connect, drag },
    selected,
    actions: { setProp },
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  const containerRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useResponsive(containerRef as React.RefObject<HTMLElement>);

  const safeRating = rating || 0;

  // Use shared style config
  const styles = getTestimonialStyles(isMobile);

  return (
    <Box
      ref={(ref: HTMLDivElement | null) => {
        if (ref) {
          connect(drag(ref));
          (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = ref;
        }
      }}
      sx={{
        p: `${styles.padding}px`,
        bgcolor: backgroundColor,
        borderRadius: `${borderRadius}px`,
        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
        overflow: "hidden",
        width: "100%",
        boxSizing: "border-box",
        minWidth: 0,
        ...(selected && selectedOutlineSx),
      }}
    >
      {safeRating > 0 && (
        <Box sx={{ display: "flex", gap: 0.25, mb: 2, flexWrap: "wrap" }}>
          {[...Array(5)].map((_, i) => (
            <Box
              key={i}
              component="svg"
              sx={{
                width: styles.stars.size,
                height: styles.stars.size,
                color: i < safeRating ? "#facc15" : "#d1d5db",
                flexShrink: 0,
              }}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ position: "relative", minWidth: 0 }}>
        {showQuotes && (
          <Typography
            component="span"
            sx={{
              position: "absolute",
              top: styles.quoteMark.top,
              left: styles.quoteMark.left,
              fontSize: styles.quoteMark.fontSize,
              opacity: 0.2,
              color: textColor,
              lineHeight: 1,
            }}
          >
            "
          </Typography>
        )}
        <Element
          id="testimonial-quote"
          is={Text}
          text={quote}
          fontSize={16}
          fontStyle="italic"
          tagName="p"
          color={textColor}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: `${styles.author.gap}px`,
          minWidth: 0,
          mt: 2,
        }}
      >
        {authorImage && (
          <Box
            component="img"
            src={authorImage}
            alt={authorName}
            sx={{
              width: styles.author.imageSize,
              height: styles.author.imageSize,
              borderRadius: "50%",
              objectFit: "cover",
              flexShrink: 0,
            }}
          />
        )}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Element
            id="testimonial-author"
            is={Text}
            text={authorName}
            fontSize={16}
            fontWeight="semibold"
            tagName="span"
            color={textColor}
          />
          <Element
            id="testimonial-title"
            is={Text}
            text={authorTitle || "Patient"}
            fontSize={14}
            tagName="span"
            color={textColor}
          />
        </Box>
      </Box>
    </Box>
  );
};

export const TestimonialSettings = () => {
  const {
    actions: { setProp },
    authorImage,
    rating,
    backgroundColor,
    textColor,
    showQuotes,
    borderRadius,
  } = useNode((node) => ({
    authorImage: node.data.props.authorImage,
    rating: node.data.props.rating,
    backgroundColor: node.data.props.backgroundColor,
    textColor: node.data.props.textColor,
    showQuotes: node.data.props.showQuotes,
    borderRadius: node.data.props.borderRadius,
  }));

  return (
    <SettingsSection>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
        💡 คลิกที่ตัวอักษรบน canvas เพื่อแก้ไข
      </Typography>

      <ImageUpload
        value={authorImage || ""}
        onChange={(url) => setProp((props: TestimonialProps) => (props.authorImage = url || undefined))}
        label="Author Image (optional)"
      />

      <div>
        <label style={labelStyle}>Rating</label>
        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => setProp((props: TestimonialProps) => (props.rating = star === rating ? 0 : star))}
              style={{
                padding: "2px",
                background: "none",
                border: "none",
                cursor: "pointer",
                transition: "transform 0.1s",
              }}
            >
              <svg
                style={{
                  width: 28,
                  height: 28,
                  color: star <= rating ? "#facc15" : "#d1d5db",
                }}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            </button>
          ))}
          <span style={{ marginLeft: "8px", fontSize: "0.875rem", color: "#6b7280" }}>
            {rating === 0 ? "No rating" : `${rating} star${rating > 1 ? "s" : ""}`}
          </span>
        </div>
      </div>

      <CheckboxField
        label="Show Quote Marks"
        checked={showQuotes}
        onChange={(v) => setProp((props: TestimonialProps) => (props.showQuotes = v))}
      />

      <ColorField
        label="Background Color"
        value={backgroundColor}
        onChange={(v) => setProp((props: TestimonialProps) => (props.backgroundColor = v))}
      />

      <ColorField
        label="Text Color"
        value={textColor}
        onChange={(v) => setProp((props: TestimonialProps) => (props.textColor = v))}
      />

      <SliderField
        label="Border Radius"
        value={borderRadius}
        onChange={(v) => setProp((props: TestimonialProps) => (props.borderRadius = v))}
        min={0}
        max={24}
        unit="px"
      />
    </SettingsSection>
  );
};

Testimonial.craft = {
  displayName: "Testimonial",
  isCanvas: true,
  props: {
    quote:
      "The care I received at this clinic was exceptional. The staff was professional and the results exceeded my expectations.",
    authorName: "Sarah M.",
    authorTitle: "Patient",
    rating: 5,
    backgroundColor: "#ffffff",
    textColor: "#1f2937",
    showQuotes: true,
    borderRadius: 12,
  },
  rules: {
    canMoveIn: () => false,
    canMoveOut: () => false,
  },
  related: {
    settings: TestimonialSettings,
  },
};
